import { apiFetch, serverMessage, serviceBase } from './http';
import type { Profile, Session } from '../types';

/* ЛИЧНОСТЬ И ПРОФИЛЬ — user-api за гейтвеем.

       GET  {gw}/user-api.application/api/v1/read/me        → { id, role, … }
       GET  {gw}/user-api.application/api/v1/read/personal   → { name, surname, … }
       POST {gw}/user-api.application/api/v1/write/personal  → 200

   ДВА ЧТЕНИЯ, А НЕ ОДНО, и это не лишний запрос: `read/me` — дешёвая ручка
   сессии (id, роль, события), `read/personal` спрашивает Keycloak живьём. Так же
   разделено в приложении врача. Кабинету нужны оба: первый отвечает «вошёл ли»,
   второй — «как зовут».

   ЗАПИСЬ MULTIPART, А НЕ JSON, и это не наш выбор: ручка объявлена через
   `Form(...)`, потому что тем же запросом врач шлёт фото. `FormData` браузер
   сериализует сам — заголовок `Content-Type` с `boundary` ставить руками НЕЛЬЗЯ,
   иначе граница не совпадёт с телом и сервер не разберёт ни одного поля.

   ШЛЁМ ТОЛЬКО ИЗМЕНЁННОЕ. Непереданное поле остаётся как было — так устроен
   контракт. Значит email и фото не затираются правкой имени.

   ПОЧТУ ТОЖЕ НЕ ОТПРАВЛЯЕМ: она способ входа, и сервер её отбивает (403). Смена
   почты — код на новый адрес через гейтвей, см. lib/account.ts.

   ТЕЛЕФОН НЕ ОТПРАВЛЯЕМ НИКОГДА. Для пациента `phoneNumber` — это ЛОГИН: по нему
   его находит телефонный плагин Keycloak. Сервер такую попытку отбивает (403), и
   правильно: смена номера означала бы передачу аккаунта владельцу нового номера.
   Поэтому в профиле телефон только читается. */

const USER_API = 'user-api';

/** Кто вошёл, или null. Никогда не бросает: любой отказ — «не вошёл». */
export async function readSession(): Promise<Session | null> {
    const base = serviceBase(USER_API);
    if (base === null) return null;
    try {
        const res = await apiFetch(base, '/read/me');
        if (!res.ok) return null;
        const body = await res.json();
        const id = typeof body?.id === 'string' ? body.id : '';
        // Пустой `id` — не «вошёл»: 200 без личности значит, что ответ не тот, за
        // который мы его принимаем.
        if (!id) return null;
        return { id, role: typeof body?.role === 'string' ? body.role : '' };
    } catch {
        return null;
    }
}

const EMPTY: Profile = { surname: '', name: '', fathername: '', phone: '', email: '' };

/** Личные данные. Пустые поля — законное состояние: у аккаунта, вошедшего по
    номеру, имени нет, пока пациент его не укажет. */
export async function readProfile(): Promise<Profile> {
    const base = serviceBase(USER_API);
    if (base === null) return EMPTY;
    try {
        const res = await apiFetch(base, '/read/personal');
        if (!res.ok) return EMPTY;
        const body = await res.json();
        return {
            surname: text(body?.surname),
            name: text(body?.name),
            fathername: text(body?.fathername),
            phone: text(body?.phone),
            email: text(body?.email),
        };
    } catch {
        return EMPTY;
    }
}

export type SaveResult = { ok: true } | { ok: false; message: string };

/** Сохранить ФИО. Три поля, ни одного больше. */
export async function saveProfile(p: Pick<Profile, 'surname' | 'name' | 'fathername'>): Promise<SaveResult> {
    const base = serviceBase(USER_API);
    if (base === null) return { ok: false, message: 'сохранение недоступно в этом окружении' };

    const form = new FormData();
    form.append('surname', p.surname.trim());
    form.append('name', p.name.trim());
    form.append('fathername', p.fathername.trim());

    try {
        // `Content-Type` НЕ ставим: его вместе с boundary проставит браузер.
        const res = await apiFetch(base, '/write/personal', { method: 'POST', body: form });
        if (res.status === 401) return { ok: false, message: 'войдите заново' };
        // 403 — узел доступа роли ещё не выдан в дереве гейтвея. Для пациента это
        // «пока недоступно», а не ошибка ввода: чинится в дереве, не повтором.
        if (res.status === 403) return { ok: false, message: 'сохранение профиля пока недоступно' };
        if (res.status === 400 || res.status === 422) {
            return { ok: false, message: await serverMessage(res, 'проверьте имя и фамилию') };
        }
        if (!res.ok) return { ok: false, message: 'не удалось сохранить' };
        return { ok: true };
    } catch {
        return { ok: false, message: 'нет связи, попробуйте ещё раз' };
    }
}

function text(v: unknown): string {
    return typeof v === 'string' ? v.trim() : '';
}
